import {
  Component, ComponentInterface, h, Prop, State, Event, EventEmitter,
  Fragment,
} from '@stencil/core';
import { RangeTabs } from '@pes-ui/components/dist/types/components/product-range/types';
import type { IProductsCarouselData } from '@pes-ui/components/dist/types/components/products-carousel/types';
import { RecentlyViewedConfig } from '@pes-ui/components/dist/types/global-types/common';
import type { DDCProps } from '@pes-ui/components/dist/types/globals/ddc';
import { TProductCtas, CAASContentOverviewSlotName, PageNames, pesCatalogPageTypes, MetaTags, buildPageViewPayload, send, setupMedalliaSurvey, SprykerRangesAll } from '@pes-ui/utils';
import { PesRoute } from '@pes-ui/components/dist/collection';

import {
  MAX_RECENTLY_VIEW_PRODUCTS,
  requestRecentlyViewedProducts,
} from '../../../globals/utils';
import { MY_SCHNEIDER_CLICK_EVENT } from '../../../globals/constants';

@Component({
  tag: 'pes-app-spryker-range-container',
  shadow: true,
})
export class PesAppRangeContainer implements ComponentInterface {
  /**
   * The host of the endpoints.
   * E.g.: https://www.se.com:9090
   */
  @Prop() host!: string;

  @Prop() endpoint!: string;

  /**
   * The code of the country of the app.
   * E.g.: fr
   */
  @Prop() countryCode!: string;

  /**
   * The code of the language of the app.
   * E.g.: fr
   */
  @Prop() languageCode!: string;

  @Prop() rangeId: string;

  @Prop() showBreadcrumbs: boolean;

  /**
   * If show Documents CTA and documents list in product-card
   */
  @Prop() showDocuments?: boolean;

  /**
   * Setup productCtas logic.
   * buttons - predefined CTA buttons.
   * productProp - predefined property of product to find in list.
   */
  @Prop() productCtas: TProductCtas;

  /**
   * Show buy online button
   */
  @Prop() showBuyOnline!: boolean;

  /**
   * Show Recently Viewed section
   */
  @Prop() showRecentlyViewed!: boolean;

  /**
   * Defines the context of the app: B2B or B2C
   */
  @Prop() siteType!: 'b2b' | 'b2c';

  /**
   * This parameter contains configurations for the document listing component.
   * For more information, please visit
   * https://confluence.se.com/pages/viewpage.action?pageId=128510823
   */
  @Prop() documentListing?: Partial<DDCProps>;

  @State() mainInfo = null;

  @State() recentlyViewedConfig: RecentlyViewedConfig = null;

  @State() recentlyViewedProducts: IProductsCarouselData = {
    sectionTitle: '',
    data: [],
    versionLabel: '',
  };
  @State() metaTags: MetaTags = null;

  /**
   * Fired on navigation.
   * Contains page parameter as event.detail.
   */
  @Event({
    eventName: MY_SCHNEIDER_CLICK_EVENT,
    composed: true,
    cancelable: true,
    bubbles: true,
  })
    pesLinkClick: EventEmitter<PesRoute>;

  tabs: RangeTabs;

  componentWillLoad(): void {
    setupMedalliaSurvey(this.rangeId);
    this.init();
  }

  emitPageViewEvent() {
    send(
      buildPageViewPayload,
      {
        pageLanguage: this.languageCode,
        pageTitle: this.metaTags.analyticsPageData.pageTitle,
        fromWebView: this.metaTags.analyticsPageData.fromWebView,
        businessUnit: this.metaTags.analyticsPageData.businessUnit,
        template: this.metaTags.analyticsPageData.template,
        pageTopCategory: this.metaTags.analyticsPageData.pageTopCategory,
        pageCategory: this.metaTags.analyticsPageData.pageCategory,
        pageSubCategory: this.metaTags.analyticsPageData.pageSubCategory,
        pageSubSubCategory: this.metaTags.analyticsPageData.pageSubSubCategory,
        pageCategoryId: this.metaTags.analyticsPageData.pageCategoryId,
        pageSubCategoryId: this.metaTags.analyticsPageData.pageSubCategoryId,
        pageRangeId: this.metaTags.analyticsPageData.pageRangeId,
        pageProductId: this.metaTags.analyticsPageData.pageProductId,
        pageTabs: this.metaTags.googleAnalyticsLookup?.pageTabs,
        pesPageTypes: this.metaTags.googleAnalyticsLookup?.pesPageTypes,
        tabKey: window.location.hash.substring(1),
      },
    );
  }

  private async init() {
    await this.loadPresentationData();
    if (this.metaTags?.analyticsPageData) this.emitPageViewEvent();
    if (this.showRecentlyViewed && this.recentlyViewedConfig) {
      const { data, sectionTitle } = await requestRecentlyViewedProducts(
        this.recentlyViewedConfig.maxProductsToDisplay || MAX_RECENTLY_VIEW_PRODUCTS,
        this.recentlyViewedConfig.sectionTitle,
      );

      this.recentlyViewedProducts = {
        data,
        sectionTitle,
        versionLabel: this.recentlyViewedProducts.versionLabel || '',
      };
    }
  }

  async loadPresentationData(): Promise<void> {
    try {
      const { error: rangesAllError, result: rangesAllResponse } = await SprykerRangesAll.getRangesAll({
        rangeId: this.rangeId,
        languageCode: this.languageCode,
        countryCode: this.countryCode,
        brand: 'se',
        userPrograms:
            JSON.parse(this.documentListing?.userProfile)?.custom_attributes
              ?.ailPrograms || '',
      });
      if (rangesAllError || !rangesAllResponse) {
        throw rangesAllError || new Error('No response from getRangesAll');
      }
      this.mainInfo = rangesAllResponse.main;
      this.tabs = rangesAllResponse.tabs;
      this.metaTags = rangesAllResponse.metaTags;
      this.recentlyViewedConfig = rangesAllResponse.recentlyViewedConfig;
    } catch (error) {
      console.error('[PES]: Could not fetch Ranges All Data', error);
      this.pesLinkClick.emit({ page: pesCatalogPageTypes.ALL_PRODUCTS });
    }
  }

  render(): HTMLElement {
    if (!this.mainInfo) return;
    return (
      <Fragment>
        <pes-app-spryker-range
          host={this.host}
          endpoint={this.endpoint}
          countryCode={this.countryCode}
          languageCode={this.languageCode}
          rangeId={this.rangeId}
          showBreadcrumbs={this.showBreadcrumbs}
          showDocuments={this.showDocuments}
          productCtas={this.productCtas}
          showBuyOnline={this.showBuyOnline}
          siteType={this.siteType}
          mainInfo={this.mainInfo}
          tabs={this.tabs}
          documentListing={this.documentListing}
          metaTags={this.metaTags}
        >
          <slot slot={CAASContentOverviewSlotName} name={CAASContentOverviewSlotName} />
        </pes-app-spryker-range>
        <pes-products-carousel
          {...this.recentlyViewedProducts}
          hostWithLocale={`${this.host}${this.countryCode}/${this.languageCode}/`}
          pageType={PageNames.RANGE}
          pageId={this.rangeId}
          class="pes-app-range__section"
        ></pes-products-carousel>
      </Fragment>
    );
  }
}
